import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

import { PostPhaseStatuses, Steps } from '../../../../../../../../Utils/Types';
import EmptyAnswered from './EmptyAnswered';
import { EmptyComing, EmptyEnded } from '..';
import EmptyState from '../../../../../../../DesignSystem/EmptyState';
import ButtonWithLoader from '../../../../../../../ButtonWithLoader';
import { SteppablePostType } from '../../../../../../../../Utils/Types/Steps';
import { loginPath } from '../../../../../../../../Utils/routes';
import { useModal } from 'mui-modal-provider';
import EntitySubscriptionModal from '../../../../../../../EntitySubscriptionModal';
import useSpace from '../../../../../../../../Hooks/useSpace';

function PrimaryAction({ onClick, isLoggedIn }: any) {
  if (isLoggedIn) {
    return (
      <Grid>
        <ButtonWithLoader onClick={onClick} size="large" color="primary" variant="contained">
          <FormattedMessage id="SECURED_STRATEGIES.CTA" />
        </ButtonWithLoader>
      </Grid>
    );
  }

  return (
    <Grid>
      <ButtonWithLoader
        component={Link}
        to={loginPath()}
        size="large"
        color="primary"
        variant="contained"
      >
        <FormattedMessage id="AUTH.LOGIN" />
      </ButtonWithLoader>
    </Grid>
  );
}

function SecondaryAction({ isLoggedIn, hasLoggedInStrategies, onClick }: any) {
  if (!isLoggedIn && !hasLoggedInStrategies) {
    return (
      <Grid>
        <ButtonWithLoader variant="outlined" onClick={onClick} size="large" color="primary">
          <FormattedMessage id="AUTH.CONTINUE_WITHOUT_SIGNUP" />
        </ButtonWithLoader>
      </Grid>
    );
  }

  return null;
}

function StudyEmptyStates({
  post,
  stepTab,
  isLoggedIn,
  canParticipate,
  isAnswered,
  hasLoggedInStrategies,
}: any) {
  const navigate = useNavigate();
  const stepType = post.type as SteppablePostType;
  const { showModal } = useModal();
  const { space } = useSpace();

  const onClick = () => {
    const currentPath = window.location.pathname + window.location.search;
    const securedStrategiesPath = `/${window.spaceSlug}/posts/${post.slug}/secured-strategies?redirect_to=${currentPath}`;

    if (isLoggedIn && hasLoggedInStrategies && !space.entity_subscription) {
      // that means that the user is logged in but does not belong to the space
      showModal(EntitySubscriptionModal, {
        onClose: () => {
          navigate(securedStrategiesPath);
        },
      });
    } else {
      navigate(securedStrategiesPath);
    }
  };

  if (stepTab.step_type === Steps[stepType].SUBMISSION) {
    if (isAnswered) {
      return <EmptyAnswered />;
    }

    if (!canParticipate) {
      return (
        <EmptyState
          content={
            <Box
              sx={{
                mb: 3,
              }}
            >
              <Typography variant="body1">
                <FormattedMessage
                  id="SECURED_STRATEGIES.INLINE_DESCRIPTION"
                  values={{
                    b: (chunk: any) => <b>{chunk}</b>,
                  }}
                />
              </Typography>
            </Box>
          }
          icon={<Lock color="primary" />}
          action={
            <SecondaryAction
              onClick={onClick}
              isLoggedIn={isLoggedIn}
              hasLoggedInStrategies={hasLoggedInStrategies}
            />
          }
          secondaryAction={<PrimaryAction onClick={onClick} isLoggedIn={isLoggedIn} />}
        />
      );
    }
  }

  if (stepTab.dates_status === PostPhaseStatuses.COMING) {
    return <EmptyComing activeStep={stepTab} />;
  }

  if (stepTab.dates_status === PostPhaseStatuses.ENDED && !isAnswered) {
    return <EmptyEnded activeStep={stepTab} />;
  }

  return null;
}

export default StudyEmptyStates;
